"use client"

import * as React from "react"
import { useNavigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"

import {
  resolveWorkspaceLandingHref,
  useActiveBranchSlug,
  useActiveWorkspaceSlug,
  useUserBranchAccess,
} from "@/features/auth"
import { useTenantAuthFlow } from "@/features/auth/providers/tenant-auth-flow-provider"
import {
  AUTH_BOOTSTRAP_QUERY_KEY,
  AUTH_TENANT_ACCESS_RESOLVE_QUERY_KEY,
} from "@/features/auth/services/auth-query"
import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"

export type TenantContextOption = { value: string; label: string }

function uniqueOptions(options: TenantContextOption[]) {
  const seen = new Set<string>()
  return options.filter((option) => {
    if (!option.value || seen.has(option.value)) return false
    seen.add(option.value)
    return true
  })
}

/**
 * The churches and branches this user may switch between, and the two switch actions. Shared by the desktop
 * sidebar switcher and the phone header's switcher sheet so both list the same scopes and land the same way.
 */
export function useTenantContextSwitching() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { authMe } = useTenantAuthFlow()
  const workspaceSlug = useActiveWorkspaceSlug() ?? ""
  const branchSlug = useActiveBranchSlug() ?? ""
  const { accessibleBranches, canAccessBranch } = useUserBranchAccess()

  const workspaceOptions = React.useMemo(
    () =>
      uniqueOptions(
        (authMe?.workspaceAccess ?? [])
          .filter((access) => access.status === "ACTIVE")
          .map((access) => ({
            value: access.workspace.slug,
            label: access.workspace.displayName?.trim() || access.workspace.name || access.workspace.slug,
          })),
      ),
    [authMe?.workspaceAccess],
  )

  const branchOptions = React.useMemo(
    () =>
      uniqueOptions(
        accessibleBranches.map((branch) => ({
          value: branch.slug,
          label: branch.name?.trim() || branch.slug,
        })),
      ),
    [accessibleBranches],
  )

  /** Lands on the chosen church. Returns its slug when a switch started, `null` when there was nothing to do. */
  const switchWorkspace = React.useCallback(
    (nextWorkspaceSlug: string): string | null => {
      if (!authMe || !nextWorkspaceSlug || nextWorkspaceSlug === workspaceSlug) return null
      const access = authMe.workspaceAccess.find(
        (entry) => entry.status === "ACTIVE" && entry.workspace.slug === nextWorkspaceSlug,
      )
      if (!access) return null
      void queryClient.invalidateQueries({ queryKey: AUTH_BOOTSTRAP_QUERY_KEY })
      void queryClient.invalidateQueries({ queryKey: AUTH_TENANT_ACCESS_RESOLVE_QUERY_KEY })
      navigate(resolveWorkspaceLandingHref(authMe, access))
      return access.workspace.slug
    },
    [authMe, navigate, queryClient, workspaceSlug],
  )

  const switchBranch = React.useCallback(
    (nextBranchSlug: string) => {
      if (!nextBranchSlug || nextBranchSlug === branchSlug || !canAccessBranch(nextBranchSlug)) return
      navigate(buildBranchAdminUrl(nextBranchSlug, "/", workspaceSlug))
    },
    [branchSlug, canAccessBranch, navigate, workspaceSlug],
  )

  return { workspaceSlug, branchSlug, workspaceOptions, branchOptions, switchWorkspace, switchBranch }
}
